import type { OrderView } from '@khaddi/contracts';
import { Button, ErrorNote, Icon, Screen, Spinner, Text, colors, formatKm, formatTime, useAction, useApi, useAsync, useLayout, useRouter } from '@khaddi/ui';
import { useEffect, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';
import { rememberActiveOrder } from '../activeOrder';
import { BranchCard, CollectBox, CustomerCard, ItemsList, PhotoTile, StatusPill } from '../components/DeliveryParts';
import { MapIllustration } from '../components/MapIllustration';
import { useShipperNav } from '../components/ShipperNav';
import { SlideToConfirm } from '../components/SlideToConfirm';
import { mapsUrl, openExternal } from '../format';
import { useProfile } from '../profile';
import { DeliveredScreen } from './DeliveredScreen';

const POLL_MS = 5000;
const MAP_HEIGHT = 300;
const SHEET_OVERLAP = 40;

const etaMinutes = (order: OrderView): number => {
  const untilEstimate = order.estimatedAt ? Math.round((Date.parse(order.estimatedAt) - Date.now()) / 60_000) : 0;
  return untilEstimate > 0 ? untilEstimate : Math.max(3, Math.round((order.delivery?.distanceKm ?? 1) * 3));
};

const timelineAt = (order: OrderView, status: OrderView['status']): string | null => order.timeline.find((t) => t.status === status)?.at ?? null;

/** Route for one delivery: the full-screen map on phones, the same content beside the main nav on wide screens. */
export const DeliveryScreen = ({ orderId }: { orderId: string }) => {
  const { navigate } = useRouter();
  const { wide } = useLayout();
  const nav = useShipperNav('home', { wideOnly: true });
  const view = <DeliveryView orderId={orderId} onDone={() => navigate('/', { replace: true })} />;
  return wide ? (
    <Screen nav={nav} background={colors.mapBg} maxWidth={760} contentStyle={{ flexGrow: 1, paddingBottom: 0 }}>
      {view}
    </Screen>
  ) : (
    <ScrollView style={{ flex: 1, backgroundColor: colors.mapBg }} contentContainerStyle={{ flexGrow: 1 }}>
      {view}
    </ScrollView>
  );
};

export type DeliveryViewProps = {
  orderId: string;
  /** Leaves the order: after delivering, releasing it, or when it is no longer this shipper's. */
  onDone: () => void;
  /** Shown in the home screen's detail pane, where there is nothing to go back to. */
  embedded?: boolean;
};

export const DeliveryView = ({ orderId, onDone, embedded = false }: DeliveryViewProps) => {
  const api = useApi();
  const { profile, reload: reloadProfile } = useProfile();
  const order = useAsync(() => api.orders.get(orderId), [orderId], { pollMs: POLL_MS });
  const [photo, setPhoto] = useState<string | null>(null);
  const [delivered, setDelivered] = useState<OrderView | null>(null);
  const [confirmRelease, setConfirmRelease] = useState(false);

  const pickUp = useAction(async () => order.setData(await api.shipper.pickUp(orderId)));
  const deliver = useAction(async () => {
    const done = await api.shipper.deliver(orderId, photo);
    rememberActiveOrder(null);
    setDelivered(done);
    void reloadProfile();
  });
  const release = useAction(async () => {
    await api.shipper.release(orderId);
    rememberActiveOrder(null);
    onDone();
  });

  if (delivered) return <DeliveredScreen order={delivered} onDone={onDone} />;
  if (order.loading && !order.data) return <Spinner />;
  if (!order.data) return <ErrorNote message={order.error ?? 'Không tải được đơn'} onRetry={() => void order.reload()} />;

  const data = order.data;
  const mine = data.shipper?.id === profile.id;
  if (data.status === 'completed' && mine) return <DeliveredScreen order={data} onDone={onDone} alreadyDone />;
  if (!mine || data.status === 'cancelled') {
    return <OrderGone order={data} reason={data.status === 'cancelled' ? `Đơn đã bị huỷ${data.cancelReason ? `: ${data.cancelReason}` : ''}` : 'Đơn này không còn được giao cho bạn.'} onHome={onDone} />;
  }

  const delivering = data.status === 'delivering';
  const brewing = data.status === 'new' || data.status === 'brewing';
  const destination = delivering ? (data.delivery?.address ?? '') : data.branch.address;
  const pickedAt = timelineAt(data, 'delivering');

  const title = delivering ? 'Đang giao đến khách' : brewing ? 'Quán đang pha' : 'Đến quán lấy hàng';
  const pill = delivering
    ? { label: pickedAt ? `Đã lấy ${formatTime(pickedAt)}` : 'Đã lấy hàng', tone: 'positive' as const }
    : brewing
      ? { label: data.status === 'new' ? 'Chờ quán nhận' : 'Đang pha', tone: 'warning' as const }
      : { label: 'Sẵn sàng lấy', tone: 'info' as const };

  const actionError = pickUp.error ?? deliver.error ?? release.error ?? (order.error ? `Mất kết nối: ${order.error}` : null);

  return (
    <View style={{ flexGrow: 1, backgroundColor: colors.mapBg }}>
      <MapIllustration height={MAP_HEIGHT} />
      <View style={{ position: 'absolute', top: 16, left: 16, right: 16, flexDirection: 'row', justifyContent: embedded ? 'flex-end' : 'space-between', zIndex: 2 }}>
        {embedded ? null : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Quay lại"
            onPress={onDone}
            style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: colors.surface, alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(28, 69, 95,.12)' }}
          >
            <Icon name="chevronLeft" size={22} color={colors.text} strokeWidth={2.2} />
          </Pressable>
        )}
        <Pressable
          accessibilityRole="link"
          accessibilityLabel={`Mở Google Maps chỉ đường đến ${destination}`}
          onPress={() => openExternal(mapsUrl(destination))}
          style={{ height: 44, borderRadius: 22, backgroundColor: colors.navy, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', gap: 8 }}
        >
          <Icon name="navigation" size={18} color="#FFFFFF" />
          <Text variant="bodyStrong" color={colors.textOnDark} style={{ fontSize: 13 }}>
            Mở Google Maps
          </Text>
        </Pressable>
      </View>

      <View style={{ marginTop: MAP_HEIGHT - SHEET_OVERLAP, flex: 1, backgroundColor: colors.surface, borderTopLeftRadius: 26, borderTopRightRadius: 26, paddingTop: 18, paddingHorizontal: 20, paddingBottom: 24, gap: 14 }}>
        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <View style={{ flex: 1 }}>
            <Text variant="caption" numeric>
              Đơn #{data.code} · {formatKm(data.delivery?.distanceKm ?? 0)} · ~{etaMinutes(data)} phút
            </Text>
            <View accessibilityRole="header"><Text variant="title">
              {title}
            </Text></View>
          </View>
          <StatusPill label={pill.label} tone={pill.tone} />
        </View>

        {delivering ? <CustomerCard order={data} /> : <BranchCard order={data} />}
        <ItemsList order={data} />

        {delivering ? (
          <View style={{ flexDirection: 'row', gap: 10 }}>
            <CollectBox order={data} />
            <PhotoTile photo={photo} onPicked={setPhoto} />
          </View>
        ) : (
          <>
            <CustomerCard order={data} />
            <View style={{ flexDirection: 'row' }}>
              <CollectBox order={data} />
            </View>
          </>
        )}

        {actionError ? (
          <View accessibilityRole="alert"><Text variant="caption" color={colors.danger}>
            {actionError}
          </Text></View>
        ) : null}

        <View style={{ marginTop: 'auto', gap: 8 }}>
          {delivering ? (
            <SlideToConfirm label="Trượt để xác nhận đã giao" onConfirm={() => void deliver.run()} loading={deliver.pending} />
          ) : (
            <>
              {brewing ? (
                <Text variant="caption" style={{ textAlign: 'center' }}>
                  Chờ quán pha xong rồi mới lấy được hàng · tự cập nhật
                </Text>
              ) : null}
              <Button label="Đã lấy hàng" icon="bag" onPress={() => void pickUp.run()} loading={pickUp.pending} disabled={brewing} />
              <Button
                label={confirmRelease ? 'Bấm lần nữa để trả đơn' : 'Trả đơn'}
                variant={confirmRelease ? 'outline' : 'ghost'}
                size="md"
                loading={release.pending}
                onPress={() => (confirmRelease ? void release.run() : setConfirmRelease(true))}
                accessibilityLabel="Trả đơn cho shipper khác"
              />
            </>
          )}
        </View>
      </View>
    </View>
  );
};

const OrderGone = ({ order, reason, onHome }: { order: OrderView; reason: string; onHome: () => void }) => {
  useEffect(() => rememberActiveOrder(null), []);
  return (
    <View style={{ flex: 1, padding: 24, justifyContent: 'center', gap: 16 }}>
      <Text variant="title">Đơn #{order.code}</Text>
      <Text variant="body" color={colors.textMuted}>
        {reason}
      </Text>
      <Button label="Về trang nhận đơn" onPress={onHome} />
    </View>
  );
};
